import {AxiosResponse} from "axios";
import {Response, PaginationResponse} from "../../types/api";
import {Auction} from "../../types/auction";
import {BidItemPrice, BidsResponseData, PlaceBidData} from "../../types/bids";
import {DictionaryObject} from "../../types/dictionary";
import {CompanyExists, NewAuctionForm, ProductsObject} from "../../types/newAuction";
import {ParticipantStatusData, ParticipateData} from "../../types/participant";
import protectedInstance from "../instances/protected";

async function getAuctions(
    companyId: number, text: string, page: number, pageSize: number,
    auctionsEndpoint: string, auctionType?: string, auctionState?: string[]
) {
    const query = `query=${text}&page=${page}&page_size=${pageSize}` +
        `&type=${auctionType ? auctionType : ""}&state=${auctionState?.join(',') ?? ''}`;

    return protectedInstance
        .get(`/company/${companyId}/auctions/${auctionsEndpoint}?${query}`)
        .then((res: AxiosResponse<PaginationResponse<Auction[]>>) => res.data)
}

async function getAuction(companyId: number, auctionId: number) {
    return protectedInstance
        .get(`/company/${companyId}/auction/${auctionId}`)
        .then((res: AxiosResponse<Response<Auction>>) => res.data)
}

async function signAuction(companyId: number, auctionId: number, signature: string) {
    return protectedInstance
        .post(
            `/company/${companyId}/auction/${auctionId}/passport/execute`, signature,
            {
                headers: {
                    'Content-Type': 'text/xml'
                }
            }
        )
        .then((res: AxiosResponse<Response<{ errorMessage: string | null }>>) => res.data)
}

async function createAuction(companyId: number, data: NewAuctionForm) {
    return protectedInstance
        .post(`/company/${companyId}/auction`, data)
        .then((res: AxiosResponse<Response<Auction>>) => res.data)
}

async function editAuction(companyId: number, auctionId: string, data: NewAuctionForm) {
    return protectedInstance
        .put(`/company/${companyId}/auction/${auctionId}`, data)
        .then((res: AxiosResponse<Response<Auction>>) => res.data)
}

async function searchProducts(
    productType: DictionaryObject, searchText: string, page: number, pageSize: number
) {
    const query = `type=${productType.value}&query=${searchText}` +
        `&page=${page}&page_size=${pageSize}`;

    return protectedInstance
        .get(`/data/items/russian?${query}`)
        .then((res: AxiosResponse<PaginationResponse<ProductsObject[]>>) => res.data)
};

async function checkCompanyExists(itn: string) {
    return protectedInstance
        .get(`/check/company/bin/${itn}`)
        .then((res: AxiosResponse<Response<CompanyExists>>) => res.data)
}

async function getFileToken(auctionId: number, companyId: number, fileId: number) {
    return protectedInstance
        .get(`/auction/${auctionId}/company/${companyId}/file/${fileId}`)
        .then((res: AxiosResponse<any>) => res.data)
}

async function changeParticipantStatus(
    companyId: number, auctionId: number, data: ParticipantStatusData
) {
    return protectedInstance
        .post(`/company/${companyId}/auction/${auctionId}/participation/status`, data)
        .then((
            res: AxiosResponse<Response<{ errorMessage: string | null }> &
                { errorMessage: string | null }>
        ) => res.data)
}

async function cancelAuction(companyId: number, auctionId: number) {
    return protectedInstance
        .post(`/company/${companyId}/auction/${auctionId}/cancel`)
        .then((res: AxiosResponse<Response<any>>) => res.data)
}

async function saveParticipationReview(companyId: number, auctionId: number, formData: FormData) {
    return protectedInstance
        .post(`/company/${companyId}/auction/${auctionId}/participation/review`, formData)
        .then((res: AxiosResponse<Response<any>>) => res.data)
}

async function getBids(companyId: number, auctionId: number) {
    return protectedInstance
        .get(`/company/${companyId}/auction/${auctionId}/bids`)
        .then((res: AxiosResponse<Response<BidsResponseData>>) => res.data)
}

async function placeSteppingBid(
    auctionId: number,
    data: { companyId: number; biddingKey: number }
) {
    return protectedInstance
        .post(`/auction/${auctionId}/step_bid`, data)
        .then((res: AxiosResponse<Response<{ errorMessage: null }>>) => res.data)
}

async function placeBid(auctionId: number, data: FormData) {
    return protectedInstance
        .post(`/auction/${auctionId}/bid`, data, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        .then((res: AxiosResponse<Response<{ errorMessage: null }>>) => res.data)
}

async function participate(auctionId: number, formData: FormData) {
    return protectedInstance
        .post(`/auction/${auctionId}/participate`, formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
        .then((
            res: AxiosResponse<Response<{ errorMessage: string | null }> &
                { errorMessage: string | null }>
        ) => res.data)
}

async function deleteLot(companyId: number, auctionId: number, lotId: number) {
    return protectedInstance
        .delete(`/company/${companyId}/auction/${auctionId}/lot/${lotId}`)
        .then((res: AxiosResponse<Response<any>>) => res.data)
}

async function addLot(companyId: number, auctionId: number, lotIds: number[]) {
    return protectedInstance
        .post(`/company/${companyId}/auction/${auctionId}/lot`,
            lotIds
        )
        .then((res: AxiosResponse<Response<any>>) => res.data)
}

export const auctionService = {
    getAuctions,
    getAuction,
    signAuction,
    createAuction,
    editAuction,
    searchProducts,
    checkCompanyExists,
    getFileToken,
    changeParticipantStatus,
    cancelAuction,
    saveParticipationReview,
    getBids,
    placeSteppingBid,
    placeBid,
    participate,
    deleteLot,
    addLot
}